import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencySelect } from "@/components/ui/currency-select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { useCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { handleSignOut } from "./ProfileMenu";
import { useTranslation } from 'react-i18next';

type UserRole = "cinematographer" | "gaffer" | "key_grip" | "producer" | "other";

type FormData = {
  full_name: string;
  company?: string;
  website?: string;
  role?: UserRole;
  default_currency?: string;
};

interface OnboardingFormProps {
  onComplete: () => void;
  isProfileComplete?: boolean;
}

export function OnboardingForm({ onComplete, isProfileComplete = false }: OnboardingFormProps) {
  const { user } = useAuth();
  const { setCurrency } = useCurrency();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  
  const { register, handleSubmit, setValue, reset } = useForm<FormData>();
  const { t, i18n } = useTranslation();

  // Initialize language from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Fetch existing profile data if already completed
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, company, website, role, default_currency")
        .eq("id", user.id)
        .single();
      
      if (error) {
        console.error("Error fetching profile data:", error);
        return;
      }
      
      if (data) {
        reset({
          full_name: data.full_name || "",
          company: data.company || "",
          website: data.website || "",
          role: data.role as UserRole || undefined,
          default_currency: data.default_currency || "USD"
        });
        
        // For the Select components which aren't directly handled by react-hook-form
        if (data.role) {
          setValue("role", data.role as UserRole);
        }
        if (data.default_currency) {
          setSelectedCurrency(data.default_currency);
          setValue("default_currency", data.default_currency);
        }
      }
    };
    
    fetchProfileData();
  }, [user, reset, setValue]);

  const onSubmit = async (data: FormData) => {
    if (!user) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          ...data,
          default_currency: selectedCurrency,
          onboarding_completed: true,
        })
        .eq("id", user.id);

      if (error) throw error;
      
      // Update the currency context
      setCurrency(selectedCurrency);
      
      toast.success(isProfileComplete ? t('profile_updated') : t('profile_completed'));
      
      // Ensure proper cleanup and callback execution
      setTimeout(() => {
        onComplete();
      }, 100);
    } catch (error: any) {
      toast.error(t('error_updating_profile', { reason: error.message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setIsDeletingAccount(true);
    setDeleteError(null);

    try {
      // Insert a deletion request for this user
      const { error } = await supabase
        .from('deletion_requests')
        .insert({
          user_email: user.email,
        });

      if (error) {
        throw new Error(error.message || "Failed to submit deletion request");
      }
      setTimeout(() => handleSignOut(t), 3000);
      toast.success(t('account_deletion_requested'));
    } catch (error: any) {
      console.error("Error requesting account deletion:", error);
      setDeleteError(error.message || t('unexpected_error'));
      toast.error(t('error_requesting_account_deletion', { reason: error.message }));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Handle form cancellation
  const handleCancel = () => {
    console.log("Form cancelled, calling onComplete");
    onComplete();
  };

  const formTitle = isProfileComplete ? t('edit_your_profile') : t('complete_your_profile');
  const buttonText = isProfileComplete ? t('save_changes') : t('complete_profile');

  return (
    <div className="bg-background p-8 rounded-lg shadow-lg w-full max-w-md mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold">{formTitle}</h2>
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          ✕
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">
        {isProfileComplete 
          ? t('update_profile_information')
          : t('help_personalize_experience')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="full_name">{t('full_name')}</Label>
          <Input
            id="full_name"
            {...register("full_name")}
            placeholder={t('full_name_placeholder')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">{t('company_optional')}</Label>
          <Input
            id="company"
            {...register("company")}
            placeholder={t('company_name_placeholder')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">{t('role')}</Label>
          <Select onValueChange={(value: UserRole) => setValue("role", value)}>
            <SelectTrigger>
              <SelectValue placeholder={t('select_your_role')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="producer">{t('producer')}</SelectItem>
              <SelectItem value="cinematographer">{t('cinematographer')}</SelectItem>
              <SelectItem value="gaffer">{t('gaffer')}</SelectItem>
              <SelectItem value="key_grip">{t('key_grip')}</SelectItem>
              <SelectItem value="other">{t('other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="default_currency">{t('default_currency')}</Label>
          <CurrencySelect
            value={selectedCurrency}
            onValueChange={(value) => {
              setSelectedCurrency(value);
              setValue("default_currency", value);
            }}
            placeholder={t('select_default_currency')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">{t('website_optional')}</Label>
          <Input
            id="website"
            {...register("website")}
            placeholder={t('website_placeholder')}
          />
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleCancel} className="flex-1">
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={isLoading} className="flex-1">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isProfileComplete ? t('saving') : t('completing')}
              </>
            ) : (
              buttonText
            )}
          </Button>
        </div>
      </form>

      <div className="mt-8 pt-6 border-t">
        <h3 className="text-destructive font-semibold mb-4">{t('danger_zone')}</h3>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="w-full">
              {t('delete_account')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('are_you_sure')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('delete_account_warning')}
                {deleteError && (
                  <p className="mt-2 text-destructive font-medium">
                    {t('error')}: {deleteError}
                  </p>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isDeletingAccount}
              >
                {isDeletingAccount ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('deleting')}
                  </>
                ) : (
                  t('delete_account')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
